import {
  getInstallerDashboardFundedProjects as getInstallerDashboardFundedProjectsAPI,
  getInstallerDashboardProjectsSummary as getInstallerDashboardProjectsSummaryAPI,
} from './apis/ProjectsAPI';
import { applicationAPI } from './apis/ApplicationAPIs';
import {
  DashboardApplication,
  DashboardChangeOrder,
  DashboardFundedProject,
  InstallerDashboardProjectsSummary,
  InstallerDashboardApplicationsSummary,
  FundedProjectMessage,
  Task,
  LoanVersion,
} from '../../../../types/api';
import { PROJECT_MILESTONE, EXTERNAL_ACTIONS } from '../constants';

export const getInstallerDashboardFundedProjects = async (
  accountId: string,
  accountType: string
): Promise<DashboardFundedProject[]> => {
  const result = await getInstallerDashboardFundedProjectsAPI(
    accountId,
    accountType
  );

  const projects: DashboardFundedProject[] = [];

  for (const proj of result.records) {
    let consultantAccount;
    if (proj.Consultant_Account__c) {
      consultantAccount = {
        id: proj.Consultant_Account__c,
        business_name: proj.Consultant_Account__r.Business_Name__c,
      };
    }

    const errors: FundedProjectMessage[] = JSON.parse(proj.Errors__c);

    let installerAccount;
    if (proj.Installer_Account__r) {
      installerAccount = {
        id: proj.Installer_Account__c,
        business_name: proj.Installer_Account__r.Business_Name__c,
      };
    }

    let installerProgram;
    if (proj.Installer_Program__r) {
      installerProgram = {
        amount_of_available_funds:
          proj.Installer_Program__r.Amount_of_Available_Funds__c,
        type: proj.Installer_Program__r.Type_of_Program__c,
      };
    }

    let application, line1, line2, city, state, zip;
    if (proj.Loan_Application__r) {
      application = {
        id: proj.Loan_Application__c,
        created_date: proj.Loan_Application__r.CreatedDate,
        project_nickname: proj.Loan_Application__r.Project_Nickname__c,
      };
      line1 = proj.Loan_Application__r.Sys_Prop_Address__c;
      line2 = proj.Loan_Application__r.Sys_Prop_Address_Line_2__c;
      city = proj.Loan_Application__r.Sys_Prop_Address_City__c;
      state = proj.Loan_Application__r.Sys_Prop_Address_State__c;
      zip = proj.Loan_Application__r.Sys_Prop_Address_Zip_Code__c;
    }

    const fp: DashboardFundedProject = {
      id: proj.Id,
      active_milestone: proj.Active_Milestone__c,
      borrower_business_name: proj.Borrower_Business_Name__c,
      borrower_contact_name: proj.Borrower_Contact_Name__c,
      closed_message: proj.Closed_Message__c,
      consultant_account: consultantAccount,
      custom_information: proj.Custom_Information__c,
      days_since_note_date: proj.Days_Since_Note_Date__c,
      days_until_expiration_date: proj.Days_Until_Expiration_Date__c,
      days_until_pricing_expiration_date:
        proj.Days_Until_Pricing_Expiration_Date__c,
      decision_date: proj.Decision_Date__c,
      errors: errors,
      external_status: proj.External_Status__c,
      final_completion_expiration_date:
        proj.Final_Completion_Expiration_Date__c,
      financing_expiration_date: proj.Financing_Expiration_Date__c,
      installer_account: installerAccount,
      installer_program: installerProgram,
      loan_app_loan_amount: proj.Loan_App_Loan_Amount__c,
      loan_app_number: proj.Loan_App_Number__c,
      loan_application: application,
      name: proj.Name,
      net_funding: proj.Net_Funding__c,
      paid_to_date: proj.Paid_To_Date__c,
      pricing_expiration_date: proj.Pricing_Expiration_Date__c,
      system_location_address: line1,
      system_location_address_2: line2,
      system_location_address_3: city + ', ' + state + ' ' + zip,
    };
    projects.push(fp);
  }

  return projects;
};

export const getInstallerDashboardApplications = async (
  accountId: string,
  accountType: string,
  installerRole: string,
  contactId: string
): Promise<DashboardApplication[]> => {
  const result = await applicationAPI.getInstallerDashboardApplications(
    accountId,
    accountType,
    installerRole,
    contactId
  );

  const applications: DashboardApplication[] = [];

  for (const rec of result.applications.records) {
    const app: DashboardApplication = {
      id: rec.Id,
      amortization_years: rec.Product__r?.Amortization_Years__c,
      app_submission_deadline: rec.App_Submission_Deadline_Date__c,
      apr_percentage: rec.Product__r?.APR_Percentage__c,
      borrower_advance: rec.Selected_Milestone_Advance_Program__c,
      borrower_contact_full_name: rec.Borrower_Contact_Full_Name__c,
      borrower_business_name: rec.Borrower_Business_Name__c,
      borrower_business_phone_number:
        rec.Borrower_Contact__r?.Phone ?? rec.Borrower_Business_Phone_Number__c,
      borrower_contact_email:
        rec.Borrower_Contact__r?.Email ?? rec.Borrower_Contact_Email__c,
      borrower_contact_first_name: rec.Borrower_Contact__r?.FirstName ?? null,
      borrower_contact_last_name: rec.Borrower_Contact__r?.LastName ?? null,
      borrower_contact_position_title:
        rec.Borrower_Contact__r?.Position_Title__c ?? null,
      borrower_account_id: rec.Borrower_Account__c ?? null,
      borrower_contact_id: rec.Borrower_Contact__c ?? null,
      is_flagged_non_profit_by_installer:
        rec.Is_Flagged_Non_Profit_By_Installer__c ?? null,
      sys_prop_address_state: rec.Sys_Prop_Address_State__c ?? null,
      can_create_change_request: false,
      change_orders: [],
      closed_reason: rec.Closed_Reason__c,
      closed_sub_reason: rec.Closed_Sub_Reason__c,
      consultant_account: rec.Consultant_Account__c,
      external_status_action: rec.External_Status_Action__c,
      external_status_installer_description:
        rec.External_Status_Installer_Description__c ?? '...',
      external_status_stage: rec.External_Status_Stage__c ?? '...',
      installer: rec.Installer__c,
      installer_program: rec.Installer_Program__c,
      internal_status: rec.Internal_Status__c,
      loan_amount: rec.loan_amount__c,
      loan_status_tasks: [],
      loan_term_years: rec.Product__r?.Loan_Term_Years__c,
      loan_version: {
        pricing_date: rec.Loan_Version__r?.Pricing_Date__c,
      } as LoanVersion,
      name: rec.Name,
      rec_info: rec.Rec_Info__c,
      system_address:
        rec.Sys_Prop_Address__c &&
        rec.Sys_Prop_Address_City__c &&
        rec.Sys_Prop_Address_State__c &&
        rec.Sys_Prop_Address_Zip_Code__c
          ? `${rec.Sys_Prop_Address__c} ${rec.Sys_Prop_Address_City__c}, ${rec.Sys_Prop_Address_State__c} ${rec.Sys_Prop_Address_Zip_Code__c}`
          : 'N/A',
      web_user_can_close: rec.Web_User_Can_Close__c,
      required_documents: rec.required_documents,
      system_size_in_k_ws: rec.System_Size_in_kWs__c,
      installer_business_name: rec.Installer_Business_Name__c,
      loan_term: {
        net_funding: null,
      },
      project_nickname: rec.Project_Nickname__c,
      project_timeline_estimation: rec.Project_Timeline_Estimation__c,
      interest_payer_during_construction:
        rec.Interest_Payer_During_Construction__c,
      deal_description_and_context: rec.Deal_Description_and_Context__c,
      project_type: rec.Project_Type__c ?? null,
      business_type_for_profit_or_non_profit:
        rec.Business_Type_For_Profit_Or_Non_Profit__c ?? null,
      for_profit_company_type: rec.For_profit_Company_Type__c ?? null,
      non_profit_organization_type: rec.Non_profit_Organization_Type__c ?? null,
      industry_borrower_reported: rec.Industry_Borrower_Reported__c ?? null,
      product: rec.Product__c ?? null,
      special_payment_percentage: rec.Special_Payment_Percentage__c ?? null,
      assets: [],
    };

    applications.push(app);
  }

  // add tasks to applications

  for (const task of result.tasks.records) {
    const new_task: Task = {
      id: task.Id,
      what: task.WhatId,
      borrower_communication: task.Borrower_Communication__c,
      status: task.Status,
      created_date: task.CreatedDate,
      last_modified_date: task.LastModifiedDate,
      system_modstamp: task.SystemModstamp,
    };
    applications
      .find((app) => app.id === task.WhatId)
      ?.loan_status_tasks.push(new_task);
  }

  // add change orders to applications

  for (const change_order of result.change_orders.records) {
    const new_change_order: DashboardChangeOrder = {
      id: change_order.Id,
      loan_application: change_order.Loan_Application__c,
      internal_status: change_order.Internal_Status__c,
      new_loan_amount: change_order.New_Loan_Amount__c,
      other_changes: change_order.Other_Changes__c,
      date_submitted: change_order.Date_Submitted__c,
      created_date: change_order.CreatedDate,
      external_status: change_order.External_Status__c,
      application_number: change_order.Application_Number__c,
      new_loan_product: change_order.New_Loan_Product__c,
      reason_for_change: change_order.Reason_For_Change__c,
      reason_for_change_solar: change_order.Reason_For_Change_Solar__c,
      additional_reason_notes_solar:
        change_order.Additional_Reason_Notes_Solar__c,
    };
    applications
      .find((app) => app.id === change_order.Loan_Application__c)
      ?.change_orders.push(new_change_order);
  }

  // process funded projects

  for (const funded_project of result.funded_projects.records) {
    if (funded_project.expr0 > 0) {
      const app = applications.find(
        (app) => app.id === funded_project.Loan_Application__c
      );
      app && (app.can_create_change_request = true);
    }
  }

  // add loan term to applications (SOQL must SELECT the app lookup; see salesforce_service)
  for (const loan_term of result.loan_terms?.records ?? []) {
    const applicationId =
      loan_term.Application__c ?? loan_term.Loan_Application__c;
    if (!applicationId) {
      continue;
    }
    const app = applications.find((a) => a.id === applicationId);
    if (app) {
      app.loan_term = {
        net_funding: loan_term.Installer_Net_Funding__c ?? null,
      };
    }
  }

  for (const asset of result.assets?.records ?? []) {
    const applicationId = asset.Loan_Application__c;
    if (!applicationId) continue;
    const app = applications.find((item) => item.id === applicationId);
    if (!app) continue;
    app.assets.push({
      id: asset.Id,
      name: asset.Name ?? null,
      type: asset.Type__c ?? null,
      price: asset.Price ?? null,
    });
  }

  return applications;
};

export const getInstallerDashboardProjectsSummary = async (
  accountId: string,
  accountType: string
): Promise<InstallerDashboardProjectsSummary> => {
  const result = await getInstallerDashboardProjectsSummaryAPI(
    accountId,
    accountType
  );

  let total_paid_to_date = 0;
  let net_funding = 0;
  let projects_in_progress = 0;
  let completed_projects = 0;

  // We have to do this because SOQL doesn't support subqueries in the COUNT function
  for (const proj of result.records) {
    total_paid_to_date += proj.Paid_To_Date__c;
    net_funding += proj.Net_Funding__c;
    if (proj.Active_Milestone__c === PROJECT_MILESTONE.FullyFunded) {
      completed_projects++;
    } else {
      projects_in_progress++;
    }
  }

  const summary: InstallerDashboardProjectsSummary = {
    total_paid_to_date: total_paid_to_date,
    net_funding: net_funding,
    projects_in_progress: projects_in_progress,
    completed_projects: completed_projects,
  };

  return summary;
};

export const getInstallerDashboardApplicationsSummary = async (
  accountId: string,
  accountType: string,
  installerRole: string,
  contactId: string
): Promise<InstallerDashboardApplicationsSummary> => {
  const result = await applicationAPI.getInstallerDashboardApplicationsSummary(
    accountId,
    accountType,
    installerRole,
    contactId
  );

  const completed_statuses = [
    EXTERNAL_ACTIONS.LOAN_APPROVED,
    EXTERNAL_ACTIONS.LOAN_DECLINED,
    EXTERNAL_ACTIONS.EXPIRED,
    EXTERNAL_ACTIONS.CLOSED,
  ];

  // We have to do this because SOQL doesn't support subqueries in the COUNT function
  let completed_applications = 0;
  const total_applications = result.records.length;

  const uniqueApplicants = new Set(
    result.records.map((item) => item.Borrower_Account__c)
  );
  const total_applicants = uniqueApplicants.size;

  for (const app of result.records) {
    if (completed_statuses.includes(app.External_Status_Action__c)) {
      completed_applications++;
    }
  }

  const active_applications = total_applications - completed_applications;

  const summary: InstallerDashboardApplicationsSummary = {
    total_applications: total_applications,
    total_applicants: total_applicants,
    completed_applications: completed_applications,
    active_applications: active_applications,
  };

  return summary;
};
